/**
 * WebSocket Service for real-time support notifications
 * Handles WebSocket connection, message processing, and reconnection logic
 */

export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp: number;
}

export interface SupportNotification {
  type: 'support_new_message' | 'support_message_update' | 'support_new_reply' | 'support_unread_count';
  data: {
    user_id?: string;
    message_id?: string;
    status?: string;
    reply_content?: string;
    sender_role?: string;
    unread_count?: number;
    is_admin?: boolean;
  };
  timestamp: number;
}

export type WebSocketEventHandler = (message: WebSocketMessage) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  private isConnecting = false;
  private isManualClose = false;
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
  private heartbeatInterval: number | null = null;
  private heartbeatTimeout: number | null = null;

  constructor() {
    this.setupEventListeners();
  }

  /**
   * Connect to WebSocket server
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already connected or connecting
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      if (this.isConnecting) {
        // If already connecting, wait for it to complete
        const checkConnection = () => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            resolve();
          } else if (!this.isConnecting) {
            reject(new Error('Connection failed'));
          } else {
            setTimeout(checkConnection, 100);
          }
        };
        checkConnection();
        return;
      }

      const token = localStorage.getItem('auth_token');
      if (!token) {
        reject(new Error('No authentication token found'));
        return;
      }

      try {
        this.isConnecting = true;
        this.isManualClose = false;

        // Connect to backend WebSocket server
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const isDev = window.location.hostname === 'localhost' && window.location.port === '3000';
        const wsHost = isDev ? 'localhost:8080' : window.location.host;
        const wsUrl = `${protocol}//${wsHost}/api/v1/ws`;
        
        console.log(`[WS] Connecting to: ${wsUrl}`);
        console.log(`[WS] Current host: ${window.location.host}`);
        console.log(`[WS] WebSocket host: ${wsHost}`);
        console.log(`[WS] Current protocol: ${window.location.protocol}`);
        console.log(`[WS] Is dev environment: ${isDev}`);

        this.ws = new WebSocket(wsUrl);

        this.ws.onopen = () => {
          console.log('[WS] Connected successfully');
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          
          // Send authentication token
          this.ws?.send(JSON.stringify({
            type: 'auth',
            token: token
          }));

          // Start heartbeat
          this.startHeartbeat();
          
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('[WS] Failed to parse message:', error, event.data);
          }
        };

        this.ws.onclose = (event) => {
          console.log(`[WS] Connection closed: ${event.code} - ${event.reason}`);
          this.isConnecting = false;
          this.stopHeartbeat();

          if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('[WS] WebSocket error:', error);
          this.isConnecting = false;
          reject(error);
        };

      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    this.isManualClose = true;
    this.stopHeartbeat();
    
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Add event listener for specific message type
   */
  addEventListener(type: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(type)) {
      this.eventHandlers.set(type, []);
    }
    this.eventHandlers.get(type)!.push(handler);
  }

  /**
   * Remove event listener
   */
  removeEventListener(type: string, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Send message to WebSocket server
   */
  send(message: any): void {
    if (this.isConnected()) {
      this.ws!.send(JSON.stringify(message));
    } else {
      console.warn('[WS] Cannot send message - not connected');
    }
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    //console.log(`[WS] Received message: ${message.type}`, message);

    // Handle heartbeat
    if (message.type === 'pong') {
      this.resetHeartbeatTimeout();
      return;
    }

    // Call specific event handlers
    const handlers = this.eventHandlers.get(message.type);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error(`[WS] Error in event handler for ${message.type}:`, error);
        }
      });
    }

    // Call general message handlers
    const generalHandlers = this.eventHandlers.get('*');
    if (generalHandlers) {
      generalHandlers.forEach(handler => {
        try {
          handler(message);
        } catch (error) {
          console.error('[WS] Error in general event handler:', error);
        }
      });
    }
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    // Don't attempt reconnection if no auth token
    if (!this.hasAuthToken()) {
      console.log('[WS] No auth token available, skipping reconnection');
      return;
    }

    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
    
    console.log(`[WS] Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);
    
    setTimeout(() => {
      if (!this.isManualClose && this.hasAuthToken()) {
        this.connect().catch(error => {
          console.error('[WS] Reconnect failed:', error);
        });
      }
    }, delay);
  }

  /**
   * Start heartbeat to keep connection alive
   */
  private startHeartbeat(): void {
    this.stopHeartbeat();
    
    this.heartbeatInterval = setInterval(() => {
      if (this.isConnected()) {
        this.send({ type: 'ping' });
        this.resetHeartbeatTimeout();
      }
    }, 30000); // Send ping every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
      this.heartbeatTimeout = null;
    }
  }

  /**
   * Reset heartbeat timeout
   */
  private resetHeartbeatTimeout(): void {
    if (this.heartbeatTimeout) {
      clearTimeout(this.heartbeatTimeout);
    }
    
    this.heartbeatTimeout = setTimeout(() => {
      console.warn('[WS] Heartbeat timeout - closing connection');
      if (this.ws) {
        this.ws.close(1000, 'Heartbeat timeout');
      }
    }, 35000); // Wait 35 seconds for pong
  }

  /**
   * Check if authentication token exists
   */
  private hasAuthToken(): boolean {
    return !!localStorage.getItem('auth_token');
  }

  /**
   * Setup browser event listeners
   */
  private setupEventListeners(): void {
    // Reconnect on page visibility change
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && !this.isConnected() && this.hasAuthToken()) {
        this.connect().catch(error => {
          console.error('[WS] Failed to reconnect on visibility change:', error);
        });
      }
    });

    // Reconnect on window focus
    window.addEventListener('focus', () => {
      if (!this.isConnected() && this.hasAuthToken()) {
        this.connect().catch(error => {
          console.error('[WS] Failed to reconnect on focus:', error);
        });
      }
    });

    // Disconnect on page unload
    window.addEventListener('beforeunload', () => {
      this.disconnect();
    });
  }
}

// Create singleton instance
const webSocketService = new WebSocketService();

export default webSocketService;
